import React, { useState, useEffect, useRef } from 'react';
import { GlassCard } from '../../_shared/GlassCard';
import { Instagram, TrendingDown, TrendingUp, Play } from 'lucide-react';

const useCounter = (end, duration = 1400) => {
    const [count, setCount] = useState(0);
    const ref = useRef(null);
    const hasAnimated = useRef(false);
    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting && !hasAnimated.current) {
                hasAnimated.current = true;
                const start = performance.now();
                const animate = (now) => {
                    const progress = Math.min(1, (now - start) / duration);
                    const eased = 1 - Math.pow(1 - progress, 3);
                    setCount(Math.floor(end * eased));
                    if (progress < 1) requestAnimationFrame(animate);
                };
                requestAnimationFrame(animate);
            }
        }, { threshold: 0.3 });
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [end, duration]);
    return [count, ref];
};

const MonthCard = ({ month, dateRange, views, accent, trendIcon: TrendIcon, trendLabel }) => {
    const [animatedViews, ref] = useCounter(views);
    return (
        <GlassCard className={`min-h-[220px] ${accent.border}`}>
            <div ref={ref} className="flex flex-col h-full">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <p className="text-white/40 text-xs uppercase tracking-widest font-semibold mb-1">{dateRange}</p>
                        <h3 className={`text-3xl font-black ${accent.title}`}>{month}</h3>
                    </div>
                    <div className={`${accent.iconBg} p-2 rounded-xl`}>
                        <TrendIcon size={20} className={accent.iconText} />
                    </div>
                </div>
                <div className="mb-4">
                    <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">Views</p>
                    <p className="text-white text-4xl font-black">{animatedViews.toLocaleString()}</p>
                </div>
                <div className="mt-auto pt-3 border-t border-white/5">
                    <p className={`text-xs ${accent.tagText} font-semibold`}>{trendLabel}</p>
                </div>
            </div>
        </GlassCard>
    );
};

const ReelTile = ({ rank, hook, date, views, url, gradient, emoji }) => (
    <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="group relative block rounded-3xl overflow-hidden transition-transform duration-300 hover:scale-[1.03]"
        style={{ aspectRatio: '9/16' }}
    >
        <div className={`absolute inset-0 bg-gradient-to-br ${gradient}`} />
        <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-5xl mb-3 opacity-30 group-hover:opacity-0 transition-opacity duration-300">{emoji}</span>
        </div>
        <div className="absolute top-1/3 left-3 right-3">
            <div className="bg-black/60 backdrop-blur-sm rounded-xl px-3 py-2 border border-white/10">
                <p className="text-white text-xs sm:text-sm font-bold leading-tight text-center">{hook}</p>
            </div>
        </div>
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 backdrop-blur-0 group-hover:backdrop-blur-sm transition-all duration-300 flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-all duration-300">
                <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center">
                    <Play size={20} className="text-white ml-0.5" fill="white" />
                </div>
            </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/70 to-transparent">
            <p className="text-white text-sm font-bold">{views} views</p>
            <p className="text-white/60 text-[10px] font-mono mt-0.5">{date}</p>
        </div>
        <div className="absolute top-2 left-2">
            <span className="bg-black/40 backdrop-blur-md text-white text-[10px] font-bold px-2 py-0.5 rounded-full border border-white/10">#{rank}</span>
        </div>
    </a>
);

const marchReels = [
    { hook: 'YOUR WIFI IS SPYING ON YOU', date: 'Mar 13', views: '198K', gradient: 'from-purple-600 via-fuchsia-500 to-pink-500', url: 'https://www.instagram.com/p/DV00WMEjdVF/', emoji: '📡' },
    { hook: 'A GUY BUILT A FAKE MUSIC BAND', date: 'Mar 25', views: '93K', gradient: 'from-red-600 via-rose-500 to-pink-400', url: 'https://www.instagram.com/p/DWToUOrAXzC/', emoji: '🎸' },
    { hook: 'CLAUDE CODE FREE FOREVER', date: 'Mar 1', views: '54K', gradient: 'from-emerald-600 via-teal-500 to-cyan-400', url: 'https://www.instagram.com/p/DVWBg_YAQFw/', emoji: '🤖' },
    { hook: 'CLAUDE GENERATES THOUSANDS OF LEADS', date: 'Mar 7', views: '42K', gradient: 'from-yellow-600 via-amber-500 to-orange-400', url: 'https://www.instagram.com/p/DVlUtm_AROS/', emoji: '🎯' },
    { hook: 'CBSE RICKROLLED 2.5M STUDENTS', date: 'Mar 14', views: '6K', gradient: 'from-blue-600 via-indigo-500 to-violet-500', url: '#', emoji: '🎓' },
];

const aprilReels = [
    { hook: 'PREDICTION MARKET ROBBED WITH A HAIR DRYER', date: 'Apr 28', views: '14K', gradient: 'from-amber-600 via-orange-500 to-red-400', url: 'https://www.instagram.com/p/DXrm8v0AeOh/', emoji: '🎰' },
    { hook: 'BENGALURU ACCOUNTABILITY PLATFORM', date: 'Apr 16', views: '9K', gradient: 'from-cyan-600 via-sky-500 to-blue-400', url: '#', emoji: '🏛️' },
    { hook: 'POLITICIANS ARE SCARED OF THIS APP', date: 'Apr 16', views: '18K', gradient: 'from-violet-600 via-purple-500 to-fuchsia-400', url: 'https://www.instagram.com/p/DXMaqUkje0A/', emoji: '🗳️' },
    { hook: 'EVERY SCREEN RECORDER NOW USELESS', date: 'Apr 13', views: '2K', gradient: 'from-emerald-600 via-green-500 to-teal-400', url: '#', emoji: '🎥' },
    { hook: 'ELON MUSK OUTPLAYED SAM ALTMAN', date: 'Apr 3', views: '2K', gradient: 'from-rose-600 via-red-500 to-orange-400', url: '#', emoji: '⚡' },
];

export const InstagramBreakdown = () => {
    return (
        <section className="py-20 px-4">
            <div className="container mx-auto max-w-6xl">
                <div className="text-center mb-12">
                    <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full border border-fuchsia-500/30 bg-fuchsia-500/10 backdrop-blur-md mb-3">
                        <Instagram size={14} className="text-fuchsia-300 mr-2" />
                        <span className="text-sm font-medium text-fuchsia-300 tracking-wide uppercase">Instagram</span>
                    </div>
                    <h2 className="text-3xl md:text-5xl font-black text-white tracking-tight mb-3">
                        Instagram Performance
                    </h2>
                    <p className="text-white/50 max-w-2xl mx-auto">
                        Instagram delivered the bigger viral spikes (March's WiFi reel hit 198K, fake-band hit 93K) but also a sharper drop-off in April. Followers grew from 17.4K → 22.2K across the period.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
                    <MonthCard
                        month="March"
                        dateRange="Mar 1 – 31"
                        views={780000}
                        accent={{
                            border: 'border-emerald-500/20',
                            title: 'text-emerald-300',
                            iconBg: 'bg-emerald-500/20',
                            iconText: 'text-emerald-300',
                            tagText: 'text-emerald-400',
                        }}
                        trendIcon={TrendingUp}
                        trendLabel="Peak month · WiFi + fake-band carried the period"
                    />
                    <MonthCard
                        month="April"
                        dateRange="Apr 1 – 30"
                        views={279470}
                        accent={{
                            border: 'border-rose-500/20',
                            title: 'text-rose-300',
                            iconBg: 'bg-rose-500/20',
                            iconText: 'text-rose-300',
                            tagText: 'text-rose-400',
                        }}
                        trendIcon={TrendingDown}
                        trendLabel="Slowdown · -64% views vs March"
                    />
                    <MonthCard
                        month="Combined"
                        dateRange="Mar 1 – Apr 30"
                        views={1059470}
                        accent={{
                            border: 'border-fuchsia-500/20',
                            title: 'text-fuchsia-300',
                            iconBg: 'bg-fuchsia-500/20',
                            iconText: 'text-fuchsia-300',
                            tagText: 'text-fuchsia-300',
                        }}
                        trendIcon={Instagram}
                        trendLabel="+4,766 followers · 17.4K → 22.2K"
                    />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div>
                        <h3 className="text-emerald-300 font-bold text-lg mb-4 uppercase tracking-wider">Top 5 · March</h3>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-3">
                            {marchReels.slice(0, 3).map((r, i) => <ReelTile key={i} rank={i + 1} {...r} />)}
                            {marchReels.slice(3, 5).map((r, i) => <ReelTile key={i + 3} rank={i + 4} {...r} />)}
                        </div>
                    </div>
                    <div>
                        <h3 className="text-rose-300 font-bold text-lg mb-4 uppercase tracking-wider">Top 5 · April</h3>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-3">
                            {aprilReels.slice(0, 3).map((r, i) => <ReelTile key={i} rank={i + 1} {...r} />)}
                            {aprilReels.slice(3, 5).map((r, i) => <ReelTile key={i + 3} rank={i + 4} {...r} />)}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
